import { useIsEditModeSatisfied } from '@/hooks/useIsEditModeSatisfied'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import NextLink from 'next/link'
import { Column } from 'react-table'

import { Box, Flex, Link, Text, Tooltip } from '@chakra-ui/react'

import { DarkProvider, UserAvatar } from '@hivewatch/dark'

import { Switch } from '@/components/ui'
import { formatDateTime, formatTimeForTableCell } from '@/utils/formatDateTime'

import type { RuleRowData } from '../types/rulesTable'
import { getRuleHref } from '../utils/ruleRoutes'
import { RulesType } from '../utils/utils'
import { RemoveButton } from './RemoveButton'

export const columns: Column<RuleRowData>[] = [
  {
    Header: 'Created',
    disableSortBy: false,
    accessor: 'createdAt',
    Cell: ({ value }: { value: string }) => (
      <Tooltip
        hasArrow
        label={formatTimeForTableCell(value)}
        openDelay={500}
        placement='auto'
        shouldWrapChildren
      >
        <Text data-testid='rulesPage_table_createdCell' isTruncated>
          {formatDateTime(value)}
        </Text>
      </Tooltip>
    ),
  },
  {
    Header: 'Last Matched',
    disableSortBy: true,
    accessor: 'lastMatched',
    Cell: ({ value }: { value: string }) => (
      <Tooltip
        hasArrow
        label={value ? formatTimeForTableCell(value) : 'Never'}
        openDelay={500}
        placement='auto'
        shouldWrapChildren
      >
        <Text data-testid='rulesPage_table_lastMatchedCell' isTruncated>
          {value ? formatDateTime(value) : 'Never'}
        </Text>
      </Tooltip>
    ),
  },
  {
    Header: 'Automation Name',
    disableSortBy: false,
    accessor: 'ruleName',
    Cell: ({
      value,
      row,
    }: {
      value: string
      row: { original: RuleRowData }
    }) => (
      <Tooltip
        hasArrow
        label={value}
        openDelay={500}
        placement='auto'
        shouldWrapChildren
      >
        {/* Real link so right-click "Open in new tab" works. Stop the click
            reaching the row, which would otherwise navigate (or open a tab)
            a second time. */}
        <Link
          as={NextLink}
          data-testid='rulesPage_table_nameLink'
          display='block'
          href={getRuleHref(row.original.id, row.original.isV2)}
          onAuxClick={(e: React.MouseEvent) => e.stopPropagation()}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <Text data-testid='rulesPage_table_nameCell' isTruncated>
            {value}
          </Text>
        </Link>
      </Tooltip>
    ),
    width: 200,
  },
  {
    Header: 'Created By',
    disableSortBy: true,
    accessor: 'createdBy',
    Cell: ({ value }: { value: string | null }) => {
      const mode = useLightMode()
      return (
        <DarkProvider>
          <UserAvatar colorScheme={mode} name={value} />
        </DarkProvider>
      )
    },
    width: 60,
  },
  {
    Header: 'Action',
    disableSortBy: true,
    accessor: 'action',
    Cell: ({ value }: { value: string }) => (
      <Tooltip
        hasArrow
        label={value}
        openDelay={500}
        placement='auto'
        shouldWrapChildren
      >
        <Text data-testid='rulesPage_table_actionCell' isTruncated>
          {value}
        </Text>
      </Tooltip>
    ),
    width: 80,
  },
  {
    Header: 'Active',
    disableSortBy: true,
    accessor: 'active',
    Cell: ({
      value,
      onChange,
      onRemove,
    }: {
      value: RulesType
      onChange: (value: RulesType) => void
      onRemove: (value: RulesType) => void
    }) => {
      const isEditMode = useIsEditModeSatisfied()
      const { shouldEnableEditIncidentRule } = usePermissions()

      const getToggleTooltipLabel = () => {
        if (!shouldEnableEditIncidentRule) {
          return 'You do not have permission to edit automations.'
        }
        return value?.status ? 'Active' : 'Disabled'
      }

      return (
        <Flex alignItems='center' justifyContent='space-between'>
          <Tooltip
            hasArrow
            label={getToggleTooltipLabel()}
            openDelay={500}
            placement='auto'
            shouldWrapChildren
          >
            {/* Keep clicks and middle-clicks on the switch from reaching the
                row, which would otherwise open the rule. */}
            <Box
              onAuxClick={(e: React.MouseEvent) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <Switch
                backgroundColor='#2fb2b0'
                checked={value?.status}
                dataTestId={`rulesPage_table_activeToggleCell${value?.id}`}
                disabled={!shouldEnableEditIncidentRule}
                onChange={() => onChange(value)}
              />
            </Box>
          </Tooltip>
          {isEditMode && (
            <Tooltip
              hasArrow
              isDisabled={!value?.status}
              label='This automation must be inactive before it can be archived.'
              openDelay={100}
              placement='auto'
              shouldWrapChildren
            >
              <RemoveButton
                isDisabled={value?.status}
                onRemove={() => onRemove(value)}
              />
            </Tooltip>
          )}
        </Flex>
      )
    },
    width: 60,
  },
]
